package com.macrofocus.eflux.ui.table

import com.macrofocus.common.format.CPFormat
import com.macrofocus.common.properties.SimpleProperty
import com.macrofocus.common.selection.MutableSelection
import com.macrofocus.common.selection.MutableSingleSelection
import com.macrofocus.common.units.Quantity
import com.macrofocus.eflux.DataLoader
import com.macrofocus.eflux.components.link.DMZUWHSZLink
import com.macrofocus.eflux.components.link.SAPAssetViewerLink
import com.macrofocus.eflux.components.link.SalesforceLink
import com.macrofocus.eflux.components.link.tDMSLink
import com.macrofocus.eflux.network.DerivedBusColumn
import com.macrofocus.eflux.network.logical.LogicalBusId
import com.macrofocus.eflux.network.logical.logicalBusIdOf
import com.macrofocus.eflux.network.substation.Substation
import com.macrofocus.eflux.network.toFixed
import com.macrofocus.eflux.ui.data.SubstationChip
import com.macrofocus.high_d.table.DefaultTable
import com.macrofocus.high_d.table.ShowFilteredDataFrame
import js.objects.unsafeJso
import mui.icons.material.BatteryUnknownTwoTone
import mui.material.*
import mui.system.sx
import mui.x.GridRenderCellParams
import org.mkui.react.useSelection
import org.molap.dataframe.DataFrame
import org.molap.dataframe.toDataFrame
import react.FC
import react.Props
import react.ReactElement
import react.create
import web.cssom.*

external interface BusTableProps : Props {
    var dataLoader: DataLoader
    var rows: Iterable<Substation>?
    var columns: Array<DerivedBusColumn>
    var probing: MutableSingleSelection<LogicalBusId>
    var selection: MutableSelection<LogicalBusId>
    var transparent: BackgroundColor?
    var filter: ReactElement<*>
}

val BusTable = FC<BusTableProps> { props ->
    val rows = props.rows
    val (getSelection, setSelection) = useSelection(props.selection)

    if(rows != null) {
        val dataFrame: DataFrame<Substation, String, Any?> = rows.toList().toDataFrame {
            for (column in props.columns) {
                column.name to { column.getValue(this.buses!!.primaries.first()) }
            }
            "Salesforce" to { this.salesforce }
            "tDMS" to { this.tDMS }
            "DMZUWHSZ" to { this.dMZUWHSZ }
            "SAP Asset Viewer" to { this.sapAssetViewer }
        }

        val table = DefaultTable<Substation, String, Any?>()
        table.filter = props.filter
        for (column in props.columns) {
            table.setLabel(column.name, column.longName)
        }
        table.setLabel(DerivedBusColumn.logicalBusId.name, "Substation")

        table.setWidths(DerivedBusColumn.zone.name, 60, 40, 60)
        table.setWidths(DerivedBusColumn.logicalBusId.name, 130, 100, 300)
        table.setWidths(DerivedBusColumn.baskv.name, 100, 40, 100)
        table.setWidths(DerivedBusColumn.v.name, 100, 60, 100)
        table.setWidths(DerivedBusColumn.busId.name, 100, 60, 100)
        table.setWidths("Salesforce", 80, 60, 300)
        table.setWidths("tDMS", 80, 60, 300)
        table.setWidths("DMZUWHSZ", 80, 60, 300)
        table.setWidths("SAP Asset Viewer", 80, 60, 300)

        table.setGridCellRenderer("logicalBusId", { params: GridRenderCellParams ->
            if(params.value is LogicalBusId) {
                val element = SubstationChip.create() {
                    this.dataLoader = props.dataLoader
                    this.primary = true
                    this.probing = props.probing
                    this.selection = props.selection
                    this.logicalBusId = params.value
                    this.placement = TooltipPlacement.left
                }
                element
            } else if(params.value is String) {
                val element = SubstationChip.create() {
                    this.dataLoader = props.dataLoader
                    this.primary = true
                    this.probing = props.probing
                    this.selection = props.selection
                    this.logicalBusId = logicalBusIdOf(params.value)
                    this.placement = TooltipPlacement.left
                }
                element
            } else {
                Button.create { BatteryUnknownTwoTone {} }
            }
        })
        table.setGridCellRenderer("Salesforce", { params: GridRenderCellParams ->
            SalesforceLink.create {
                url = params.value as String?
                link = true
            }
        })
        table.setGridCellRenderer("tDMS", { params: GridRenderCellParams ->
            tDMSLink.create {
                url = params.value as String?
                link = true
            }
        })
        table.setGridCellRenderer("DMZUWHSZ", { params: GridRenderCellParams ->
            DMZUWHSZLink.create {
                url = params.value as String?
                link = true
            }
        })
        table.setGridCellRenderer("SAP Asset Viewer", { params: GridRenderCellParams ->
            SAPAssetViewerLink.create {
                url = params.value as String?
                link = true
            }
        })

        table.setFormat("baskv", object: CPFormat<Any?> {
            override fun format(value: Any?): String? {
                if(value is Quantity<*>) {
                    return value.amount.toFixed(0) + " " + value.unit.symbol
                } else {
                    return ""
                }
            }

            override fun formatHtml(value: Any?, htmlSupported: Boolean): String? {
                TODO("Not yet implemented")
            }

            override fun parse(text: String?): Any? {
                TODO("Not yet implemented")
            }

            override val pattern: String?
                get() = TODO("Not yet implemented")
            override val nativeFormat: Any?
                get() = TODO("Not yet implemented")
            override val horizontalAlignment: CPFormat.HorizontalAlignment?
                get() = CPFormat.HorizontalAlignment.Right

            override fun getClassName(value: Any?): String? {
                TODO("Not yet implemented")
            }
        })
        table.setFormat("v", object: CPFormat<Any?> {
            override fun format(value: Any?): String? {
                if(value is Quantity<*>) {
                    return value.amount.toFixed(2) + " " + value.unit.symbol
                } else {
                    return ""
                }
            }

            override fun formatHtml(value: Any?, htmlSupported: Boolean): String? {
                TODO("Not yet implemented")
            }

            override fun parse(text: String?): Any? {
                TODO("Not yet implemented")
            }

            override val pattern: String?
                get() = TODO("Not yet implemented")
            override val nativeFormat: Any?
                get() = TODO("Not yet implemented")
            override val horizontalAlignment: CPFormat.HorizontalAlignment?
                get() = CPFormat.HorizontalAlignment.Right

            override fun getClassName(value: Any?): String? {
                TODO("Not yet implemented")
            }
        })

        table.dataFrame = ShowFilteredDataFrame(dataFrame, dataFrame, SimpleProperty(true))

        table.nativeComponent()
        return@FC
    }

    TableContainer {
        sx = unsafeJso {
            position = Position.absolute
            top = 0.px
            bottom = 0.px
            if(props.transparent != null) {
                backgroundColor = props.transparent
            }
        }

        component = Paper.create().type

        Table {
            sx = unsafeJso {
                minWidth = 650.px
                // Enable horizontal scrolling
//                    overflowX = Auto.auto
            }

            size = Size.small

            ariaLabel = "simple table"
            stickyHeader = true

            TableHead {
                TableRow {
                    for (column in props.columns) {
                        TableCell {
                            +"${column.longName}"
                        }
                    }
                }
//                TableRow {
//                    for (column in props.columns) {
//                        TableCell {
//                            +"${column.name}"
//                        }
//                    }
//                }
            }
            if (rows != null) {
                TableBody {
                    for (row in rows) {
                        for(bus in row.buses!!.primaries) {
                            TableRow {
                                //                        key = row.toString()
                                //
                                selected = props.selection.isSelected(row.logicalBusId)
                                onMouseOver = {
                                    props.probing.selected = row.logicalBusId
                                }
                                onMouseDown = {
                                    props.selection.setSelectedElement(row.logicalBusId)
                                }

                                for (column in props.columns) {
                                    TableCell {
                                        sx {
                                            whiteSpace = WhiteSpace.nowrap
                                        }
                                        //                                //                                                        component = ReactHTML.th
                                        //                                //                                                        scope = "row"
                                        //
                                        val value = column.getValue(bus)
                                        if (value is Quantity<*> || value is Number) {
                                            align = TableCellAlign.right
                                        }
                                        val formattedValue = column.getFormattedValue(bus)
                                        if(formattedValue != null) {
                                            +formattedValue
                                        } else {
                                            if(value != null) {
                                                +(value.toString())
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
